import { Button, DialogActions } from '@fluentui/react-components'
import { useEffect, useState } from 'react'
import styles from './ProjectEconomySaveModal.module.css'
import LoadingSpinner from '../../StatusIndicators/LoadingSpinner'
import { useSaveProjectAttributes } from '../../../fetchData/mutations/useSaveProjectAttributes'
import { useSaveSheets } from '../../../fetchData/mutations/useSaveSheets'
import { useEditingContext } from '../../../context/EditingContext'
import { useInteractionContext } from '../../../context/InteractionContext'
import SaveEditsPreview from '../SaveEditsPreview/SaveEditsPreview'
import { modalTimeout } from '../../../utils/modalTimeout'

interface ProjectEconomySaveContentProps {
  onClose: () => void
  onLoadingChange: (isLoading: boolean) => void
  onErrorChange: (hasError: boolean) => void
}

const ProjectEconomySaveContent = ({
  onClose,
  onLoadingChange,
  onErrorChange,
}: ProjectEconomySaveContentProps) => {
  const { saveProjectAttributes, sheetsMutation } = useSaveProjectAttributes()
  const saveSheets = useSaveSheets()

  const { edits } = useEditingContext()
  const { setLoadProjectEconomy } = useInteractionContext()

  const [showEdits, setShowEdits] = useState<boolean>(false)
  const [saveConfirmed, setSaveConfirmed] = useState(false)

  const getErrorMessage = (error: unknown, fallback: string): string => {
    if (error && typeof error === 'object' && 'message' in error) {
      return String((error as any).message)
    }
    return fallback
  }

  const getAllErrorMessages = (): string => {
    const errors: string[] = []

    if (saveSheets.error) {
      errors.push(
        getErrorMessage(saveSheets.error, 'Kunde inte spara ändringar')
      )
    }
    if (saveProjectAttributes.error) {
      errors.push(
        getErrorMessage(
          saveProjectAttributes.error,
          'Kunde inte spara projektattribut'
        )
      )
    }
    if (sheetsMutation.error) {
      errors.push(
        getErrorMessage(sheetsMutation.error, 'Kunde inte spara data')
      )
    }

    return errors.join(', ')
  }

  // Check if any mutation is loading
  const isLoading =
    saveSheets.isPending ||
    saveProjectAttributes.isPending ||
    sheetsMutation.isPending

  // Check if any mutation has error
  const hasError =
    saveSheets.isError ||
    saveProjectAttributes.isError ||
    sheetsMutation.isError

  // Notify parent component about loading and error state
  useEffect(() => {
    onLoadingChange(isLoading && !hasError)
    onErrorChange(hasError)
  }, [isLoading, hasError, onLoadingChange, onErrorChange])

  useEffect(() => {
    if (saveConfirmed) {
      if (Object.keys(edits.projectAttributes).length > 0) {
        saveProjectAttributes.mutate()
      } else {
        saveSheets.mutate()
      }
      setSaveConfirmed(false)
    }
  }, [
    edits.projectAttributes,
    saveConfirmed,
    saveProjectAttributes,
    saveSheets,
  ])

  // Check if save was successful
  const isSheetsSuccess = saveSheets.isSuccess
  const isAttributesSuccess = saveProjectAttributes.isSuccess
  const isMutationSuccess =
    sheetsMutation.isSuccess || !sheetsMutation.isPending

  const isSuccess =
    isSheetsSuccess || (isAttributesSuccess && isMutationSuccess)

  // Auto-close modal on success
  useEffect(() => {
    if (isSuccess) {
      modalTimeout(() => onClose())
      setLoadProjectEconomy(true)
    }
  }, [isSuccess, onClose, setLoadProjectEconomy])

  if (isLoading && !hasError) {
    return (
      <div className={`${styles.messageContainer} ${styles.spinnerContainer}`}>
        <LoadingSpinner text={'Projektekonomin sparas...'} />
      </div>
    )
  }

  if (hasError) {
    const errorMessages = getAllErrorMessages()

    return (
      <div className={styles.confirmBody}>
        <p>
          Ett fel uppstod vid sparning <br />
          <b>{errorMessages}</b>
        </p>

        <DialogActions>
          <Button
            appearance="primary"
            onClick={() => {
              saveSheets.reset()
              saveProjectAttributes.reset()
              sheetsMutation.reset()
              setSaveConfirmed(true)
            }}
          >
            Försök igen
          </Button>
          <Button appearance="secondary" onClick={onClose}>
            Stäng
          </Button>
        </DialogActions>
      </div>
    )
  }

  if (isSuccess) {
    return (
      <div className={`${styles.messageContainer} ${styles.spinnerContainer}`}>
        {saveSheets.data?.message && <p>{saveSheets.data.message}</p>}
        {sheetsMutation.data?.message && <p>{sheetsMutation.data.message}</p>}
        {saveProjectAttributes.data?.message && (
          <p>{saveProjectAttributes.data.message}</p>
        )}
      </div>
    )
  }

  return (
    <div className={styles.confirmBody}>
      <p>Är du säker på att du vill spara projektekonomin?</p>

      <DialogActions>
        <Button appearance="primary" onClick={() => setSaveConfirmed(true)}>
          Ja
        </Button>
        <Button appearance="secondary" onClick={onClose}>
          Avbryt
        </Button>
        <Button appearance="subtle" onClick={() => setShowEdits(!showEdits)}>
          {showEdits ? 'Dölj' : 'Visa'} ändringar
        </Button>
      </DialogActions>

      <SaveEditsPreview showEdits={showEdits} />
    </div>
  )
}

export default ProjectEconomySaveContent
